import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Check, XCircle, CalendarClock, Radio, Mic2, Copy, X } from "lucide-react";
import { api } from "@/lib/api";
import { Badge, STATUS_META, FilterTabs, timeAgo } from "./engShared";

const SCRIPT_STYLES = [["standard", "Regenerate"], ["shorter", "Shorten"], ["romantic", "Make Romantic"],
  ["energetic", "Make Energetic"], ["professional", "Make Professional"]];

export default function EngRequests() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("all");
  const [scriptFor, setScriptFor] = useState(null);
  const [script, setScript] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/engagement/requests?status=${status}`)
    .then((r) => { setItems(r.data.items || []); setCounts(r.data.counts || {}); });
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const act = async (id, action, extra = {}) => {
    try {
      const r = await api.post(`/engagement/requests/${id}/action`, { action, ...extra });
      toast.success(action === "send_to_dj" ? "Added to the AI DJ rundown queue" : "Done");
      load();
      return r.data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const genScript = async (style) => {
    setBusy("script");
    try {
      const r = await api.post(`/engagement/requests/${scriptFor.request_id}/dj-script`, { style });
      setScript(r.data.script || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Script generation failed"); }
    setBusy("");
  };

  return (
    <div className="space-y-4" data-testid="eng-requests-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Song Requests</h1>
        <p className="text-sm text-ink-secondary">Approve, schedule, generate DJ scripts and send requests to your rundown.</p>
      </div>
      <FilterTabs active={status} onChange={setStatus} counts={counts}
        tabs={[["all", "All"], ["pending", "Pending"], ["approved", "Approved"], ["scheduled", "Scheduled"], ["played", "Played"], ["rejected", "Declined"], ["flagged", "Flagged"]]} />

      <div className="rounded-xl border border-line bg-bg-surface divide-y divide-line/50">
        {!items.length && <div className="p-8 text-center text-sm text-ink-muted" data-testid="eng-requests-empty">No requests in this view.</div>}
        {items.map((r) => (
          <div key={r.request_id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`eng-request-row-${r.request_id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold">{r.song_title}</span>
                <span className="text-xs text-ink-muted">— {r.artist}</span>
                <Badge meta={STATUS_META[r.status]} />
                {(r.request_count || 1) > 1 && <span className="text-[10px] font-mono text-brand-cyan">{r.request_count}× requested</span>}
              </div>
              <div className="text-[11px] text-ink-muted mt-0.5">
                {r.listener_name || "Listener"}{r.email ? ` · ${r.email}` : ""}{r.location ? ` · ${r.location}` : ""} · {timeAgo(r.submitted_at)}
                {r.shoutout_text ? ` — "${r.shoutout_text}"` : ""}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {r.status !== "approved" && <button onClick={() => act(r.request_id, "approve")} data-testid={`eng-req-approve-${r.request_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/50 text-emerald-300 px-2.5 py-1 text-[11px]"><Check className="h-3 w-3" /> Approve</button>}
              {r.status !== "rejected" && <button onClick={() => act(r.request_id, "decline")} data-testid={`eng-req-decline-${r.request_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-rose-500/50 text-rose-300 px-2.5 py-1 text-[11px]"><XCircle className="h-3 w-3" /> Decline</button>}
              <button onClick={() => act(r.request_id, "schedule")} data-testid={`eng-req-schedule-${r.request_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-sky-500/50 text-sky-300 px-2.5 py-1 text-[11px]"><CalendarClock className="h-3 w-3" /> Schedule</button>
              <button onClick={() => act(r.request_id, "played")} data-testid={`eng-req-played-${r.request_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-[11px]"><Radio className="h-3 w-3" /> Played</button>
              <button onClick={() => { setScriptFor(r); setScript(r.dj_script || ""); }} data-testid={`eng-req-script-${r.request_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-brand-amber/50 text-brand-amber px-2.5 py-1 text-[11px]"><Mic2 className="h-3 w-3" /> DJ Script</button>
            </div>
          </div>
        ))}
      </div>

      {scriptFor && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="eng-script-modal">
          <div className="w-full max-w-lg rounded-xl border border-line bg-bg-surface p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-sm flex items-center gap-2"><Mic2 className="h-4 w-4 text-brand-amber" /> AI DJ Script</div>
              <button onClick={() => setScriptFor(null)} data-testid="eng-script-close" className="text-ink-muted hover:text-ink-primary"><X className="h-5 w-5" /></button>
            </div>
            <div className="text-xs text-ink-muted">"{scriptFor.song_title}" — {scriptFor.artist}, requested by {scriptFor.listener_name || "a listener"}</div>
            {script ? (
              <div className="rounded-lg border border-line bg-bg-elevated p-3 text-sm whitespace-pre-wrap" data-testid="eng-script-text">{script}</div>
            ) : (
              <div className="rounded-lg border border-dashed border-line p-4 text-center text-xs text-ink-muted">Pick a style below to generate the script.</div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {SCRIPT_STYLES.map(([k, label]) => (
                <button key={k} onClick={() => genScript(k)} disabled={!!busy} data-testid={`eng-script-style-${k}`}
                  className="rounded-full border border-line px-3 py-1.5 text-[11px] font-bold text-ink-secondary hover:text-ink-primary disabled:opacity-50">
                  {busy === "script" ? "…" : label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => { navigator.clipboard.writeText(script); toast.success("Copied"); }} disabled={!script} data-testid="eng-script-copy"
                className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-xs font-bold disabled:opacity-40"><Copy className="h-3.5 w-3.5" /> Copy</button>
              <button onClick={async () => { await act(scriptFor.request_id, "send_to_dj"); setScriptFor(null); }} disabled={!!busy} data-testid="eng-script-send-dj"
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-amber text-black px-4 py-2 text-xs font-bold disabled:opacity-50">
                <Radio className="h-3.5 w-3.5" /> Send to AI DJ rundown
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
